import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FileText, Plus, Pencil, Ban, MessageCircle, Trash2 } from 'lucide-react';
import { gstAPI } from '../../services/api';
import { useGst } from './GstContext';
import GstLinesEditor, { emptyLine } from './GstLinesEditor';
import { openPdf } from './gstPdf';
import GstWhatsappModal from './GstWhatsappModal';
import { dmy, inr, msg, today } from './gstUi';

export default function GstInvoicesTab() {
  const { call, callRaw } = useGst();
  const [list, setList] = useState({ data: [], total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ month: '', status: '', q: '', page: 1 });
  const [company, setCompany] = useState(null);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState('');
  const [sharing, setSharing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
    call(() => gstAPI.invoices(params)).then(setList).catch((e) => toast.error(msg(e)));
  }, [call, filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    call(() => gstAPI.company()).then((b) => setCompany(b.data)).catch(() => {});
    call(() => gstAPI.parties()).then((b) => setParties(b.data)).catch(() => {});
    call(() => gstAPI.items()).then((b) => setItems(b.data)).catch(() => {});
  }, [call]);

  const openForm = () => {
    // Same rule as the backend: a prefix is only needed when numbers are not plain.
    const needsPrefix = (company?.numberFormat || 'plain') !== 'plain';
    if (!company?.gstin || (needsPrefix && !company?.invoicePrefix)) { toast.error(needsPrefix ? 'Fill in the Company tab first (GSTIN and invoice prefix)' : 'Fill in the Company tab first (GSTIN)'); return; }
    setForm({ invoiceDate: today(), mode: 'saved', partyId: '', pricesIncludeGst: true, party: { name: '', gstin: '', stateCode: '', address: '', phone: '' }, lines: [emptyLine()], notes: '' });
  };

  const openEdit = (r) => {
    const saved = r.party?.partyId && parties.some((p) => p._id === r.party.partyId);
    setForm({
      editId: r._id, invoiceDate: String(r.invoiceDate).slice(0, 10), mode: saved ? 'saved' : 'walkin', partyId: saved ? r.party.partyId : '',
      pricesIncludeGst: r.pricesIncludeGst !== false,
      party: { name: r.party?.name || '', gstin: r.party?.gstin || '', stateCode: r.party?.stateCode || '', address: r.party?.address || '', phone: r.party?.phone || '' },
      lines: r.lines.map((l) => ({ description: l.description, hsn: l.hsn || '', unit: l.unit || 'NA', qty: l.qty, rate: l.rate, discount: l.discount || 0, gstPercent: l.gstPercent })),
      notes: r.notes || '',
    });
  };

  // Place of supply for the live preview (server decides for real).
  const buyerState = () => {
    // No state is asked for: a buyer without one is billed in the company's own state (CGST + SGST).
    if (form.mode === 'saved') return parties.find((p) => p._id === form.partyId)?.stateCode || company?.stateCode;
    const g = form.party.gstin.trim().toUpperCase();
    return /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(g) ? g.slice(0, 2) : (form.party.stateCode || company?.stateCode);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { invoiceDate: form.invoiceDate, pricesIncludeGst: form.pricesIncludeGst, lines: form.lines, notes: form.notes };
      if (form.mode === 'saved') body.partyId = form.partyId; else body.party = form.party;
      const b = await call(() => (form.editId ? gstAPI.updateInvoice(form.editId, body) : gstAPI.createInvoice(body)));
      toast.success(`Invoice ${b.data.number} ${form.editId ? 'updated' : 'created'}`);
      setForm(null); load();
    } catch (err) { toast.error(msg(err)); } finally { setBusy(false); }
  };

  const doDelete = async (r) => {
    const ok = window.confirm(`Delete bill ${r.number}?\n\nEvery later bill moves down by one number (the next new bill reuses the last number). Copies already sent to customers keep the old number. This cannot be undone.\n\nTo keep a record of a mistake, use Cancel instead.`);
    if (!ok) return;
    try {
      const b = await call(() => gstAPI.deleteInvoice(r._id));
      toast.success(b.data.shifted.length ? `Deleted ${b.data.deleted}; ${b.data.shifted.length} later bill(s) moved down` : `Deleted ${b.data.deleted}`);
      load();
    } catch (err) { toast.error(msg(err)); }
  };

  const doCancel = async () => {
    setBusy(true);
    try { await call(() => gstAPI.cancelInvoice(cancelling._id, reason)); toast.success('Invoice cancelled'); setCancelling(null); setReason(''); load(); }
    catch (err) { toast.error(msg(err)); } finally { setBusy(false); }
  };

  const setF = (p) => setForm({ ...form, ...p });
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input className="form-control" style={{ maxWidth: 170 }} type="month" value={filters.month} onChange={(e) => setFilters({ ...filters, month: e.target.value, page: 1 })} />
        <select className="form-control" style={{ maxWidth: 140 }} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}>
          <option value="">All</option><option value="active">Active</option><option value="cancelled">Cancelled</option>
        </select>
        <input className="form-control" style={{ maxWidth: 220 }} placeholder="Search number / party / GSTIN" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })} />
        <button className="btn btn-primary" onClick={openForm}><Plus size={14} /> New invoice</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>{['Number', 'Date', 'Party', 'GSTIN', 'Total', 'Status', ''].map((h) => <th key={h} style={{ textAlign: 'left', padding: 6 }}>{h}</th>)}</tr></thead>
          <tbody>
            {list.data.map((r) => (
              <tr key={r._id} style={{ borderTop: '1px solid rgba(128,128,128,.25)', opacity: r.status === 'cancelled' ? 0.55 : 1 }}>
                <td style={{ padding: 6 }}>{r.number}</td><td style={{ padding: 6 }}>{dmy(r.invoiceDate)}</td>
                <td style={{ padding: 6 }}>{r.party?.name}</td><td style={{ padding: 6 }}>{r.party?.gstin || '—'}</td>
                <td style={{ padding: 6 }}>₹{inr(r.totals?.grandTotal)}</td><td style={{ padding: 6 }}>{r.status}</td>
                <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                  <button className="btn btn-secondary" title="PDF" onClick={() => openPdf(callRaw, 'invoice', r._id)}><FileText size={14} /></button>{' '}
                  <button className="btn btn-secondary" title="Send on WhatsApp" onClick={() => setSharing(r)}><MessageCircle size={14} /></button>{' '}
                  {r.status === 'active' && <button className="btn btn-secondary" title="Edit" onClick={() => openEdit(r)}><Pencil size={14} /></button>}{' '}
                  <button className="btn btn-secondary" title="Delete bill" onClick={() => doDelete(r)}><Trash2 size={14} /></button>{' '}
                  {r.status === 'active' && <button className="btn btn-secondary" title="Cancel" onClick={() => setCancelling(r)}><Ban size={14} /></button>}
                </td>
              </tr>
            ))}
            {list.data.length === 0 && <tr><td colSpan={7} style={{ padding: 12, opacity: 0.6 }}>No invoices.</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <button className="btn btn-secondary" disabled={list.page <= 1} onClick={() => setFilters({ ...filters, page: list.page - 1 })}>Prev</button>
        <span>Page {list.page} of {list.pages} · {list.total} invoices</span>
        <button className="btn btn-secondary" disabled={list.page >= list.pages} onClick={() => setFilters({ ...filters, page: list.page + 1 })}>Next</button>
      </div>

      {form && (
        <div className="modal-overlay">
          <form className="modal" style={{ maxWidth: 1000, width: '95%' }} onSubmit={submit}>
            <div className="modal-header"><h3>{form.editId ? 'Edit tax invoice' : 'New tax invoice'}</h3><button type="button" className="modal-close" onClick={() => setForm(null)}>×</button></div>
            <div className="modal-body">
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div className="form-group"><label className="form-label">Invoice date</label><input className="form-control" type="date" max={today()} value={form.invoiceDate} onChange={(e) => setF({ invoiceDate: e.target.value })} /></div>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '24px 0 0' }}>
                  <input type="checkbox" checked={form.pricesIncludeGst} onChange={(e) => setF({ pricesIncludeGst: e.target.checked })} />
                  Amounts include GST
                </label>
                <div className="form-group"><label className="form-label">Buyer</label>
                  <select className="form-control" value={form.mode} onChange={(e) => setF({ mode: e.target.value })}><option value="saved">Saved party</option><option value="walkin">Walk-in / new buyer</option></select></div>
                {form.mode === 'saved' ? (
                  <div className="form-group" style={{ flex: 1 }}><label className="form-label">Party</label>
                    <select className="form-control" value={form.partyId} onChange={(e) => setF({ partyId: e.target.value })}>
                      <option value="">Select…</option>{parties.map((p) => <option key={p._id} value={p._id}>{p.name} {p.gstin ? `(${p.gstin})` : ''}</option>)}</select></div>
                ) : (
                  <>
                    <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={form.party.name} onChange={(e) => setF({ party: { ...form.party, name: e.target.value } })} /></div>
                    <div className="form-group"><label className="form-label">GSTIN (if registered)</label><input className="form-control" value={form.party.gstin} onChange={(e) => setF({ party: { ...form.party, gstin: e.target.value } })} /></div>
                    <div className="form-group" style={{ flex: 1 }}><label className="form-label">Address</label><input className="form-control" value={form.party.address} onChange={(e) => setF({ party: { ...form.party, address: e.target.value } })} /></div>
                    <div className="form-group"><label className="form-label">Mobile</label><input className="form-control" value={form.party.phone} onChange={(e) => setF({ party: { ...form.party, phone: e.target.value } })} /></div>
                  </>
                )}
              </div>
              <GstLinesEditor lines={form.lines} setLines={(lines) => setF({ lines })} items={items} intra={buyerState() === company?.stateCode} inclusive={form.pricesIncludeGst} />
              <div className="form-group" style={{ marginTop: 10 }}><label className="form-label">Notes (printed on the invoice)</label><input className="form-control" value={form.notes} onChange={(e) => setF({ notes: e.target.value })} /></div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : form.editId ? 'Save changes' : 'Create invoice'}</button>
            </div>
          </form>
        </div>
      )}

      {sharing && <GstWhatsappModal doc={sharing} kind="invoice" onClose={() => setSharing(null)} />}

      {cancelling && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header"><h3>Cancel {cancelling.number}?</h3><button type="button" className="modal-close" onClick={() => setCancelling(null)}>×</button></div>
            <div className="modal-body">
              <p>The number stays in the series and the invoice is marked CANCELLED. This cannot be undone.</p>
              <label className="form-label">Reason *</label>
              <input className="form-control" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCancelling(null)}>Keep invoice</button>
              <button className="btn btn-primary" disabled={busy || !reason.trim()} onClick={doCancel}>Cancel invoice</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
